import React, { useState } from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { Col, Divider, Row, Select, Tooltip } from 'antd'
import { isEmpty, isEqual, pick } from 'lodash'
import { IconExpandBox } from '@edulastic/icons'
import { withNamespaces } from '@edulastic/localization'
import { sanitizeHtml } from '@edulastic/common/src/utils/html'
import get from 'lodash/get'
import { PortalSpinner } from '@edulastic/common'
import {
  StyledBrowseIconWrapper,
  StyledDataInputWrapper,
  StyledFieldLabel,
  StyledOptionalText,
  StyledRequired,
  StyledSelectInput,
} from './styled'
import { selectsData } from '../../../../TestPage/components/common'
import {
  getAllFormattedCurriculumsSelector,
  getStandardsListSelector,
  standardsSelector,
} from '../../../../src/selectors/dictionaries'
import { getDictStandardsForCurriculumAction } from '../../../../src/actions/dictionaries'
import {
  toggleVerifyEmailModalAction,
  isUserNotVerifiedSelector,
} from '../../../../../student/Login/ducks'
import StandardsModal from '../../../../../assessment/containers/QuestionMetadata/StandardsModal'

const StandardsInput = ({
  t: i18translate,
  updateAlignmentData,
  formattedCuriculums,
  curriculumStandards,
  curriculumStandardsLoading,
  getCurriculumStandards,
  alignment,
  allCurriculums,
  curriculumsData,
  isUserNotVerified,
  toggleVerifyEmailModal,
}) => {
  const [showStandardsModal, setShowStandardsModal] = useState(false)
  const [searchProps, setSearchProps] = useState({
    curriculumId: null,
    grades: [],
    searchStr: null,
  })

  const {
    curriculum = '',
    curriculumId = null,
    grades = [],
    subject = '',
    standards = [],
  } = alignment || {}

  const {
    pdlCurriculumId,
    pdlAdoptYear,
    pdlCurriculumName = '',
    pdlSubjectLabel = '',
    pdlSubjectId,
    pdlGrades = [],
  } = curriculumsData.find(({ _id }) => _id === curriculumId) || {}

  const hasEmptyFields = !subject || !curriculumId

  const curriculumStandardsELO = curriculumStandards?.elo || []
  const standardsArr = (standards || []).map((el) => el.identifier)

  const updateSubject = (_subject) => {
    updateAlignmentData({
      subject: _subject,
      curriculumId: null,
      curriculum: '',
      standards: [],
      domains: [],
    })
  }

  const handleSearchStandard = (searchStr) => {
    const searchObject = { curriculumId, grades, searchStr }
    if (!isEqual(searchProps, searchObject)) {
      setSearchProps(searchObject)
      getCurriculumStandards(
        searchObject.curriculumId,
        searchObject.grades,
        searchObject.searchStr
      )
    }
  }

  const handleStandardSelect = (chosenStandardsArr, option) => {
    const newStandard = pick(option.props.obj, [
      '_id',
      'level',
      'grades',
      'identifier',
      'tloIdentifier',
      'tloId',
      'tloDescription',
      'eloId',
      'subEloId',
      'description',
      'curriculumId',
      'pdlId',
    ])
    let newStandards = standards.some(
      (standard) => standard._id === newStandard._id
    )
    if (newStandards) {
      newStandards = standards.filter(
        (standard) => standard._id !== newStandard._id
      )
    } else {
      newStandards = [...standards, newStandard]
    }
    updateAlignmentData({
      standards: newStandards.map((standard) => ({
        id: standard._id,
        ...standard,
      })),
    })
  }

  const handleStandardDeselect = (removedElement) => {
    const newStandards = standards.filter(
      (el) => !(el.identifier === removedElement)
    )
    updateAlignmentData({ standards: newStandards })
  }

  const handleApply = (data = {}) => {
    if (!isEmpty(data)) {
      let { subject: _subject } = data
      const { eloStandards = [] } = data
      if (!_subject) {
        const curriculumFromStandard = data.standard.id
          ? formattedCuriculums.find((c) => c.value === data.standard.id) || {}
          : {}
        _subject = curriculumFromStandard?.subject
      }
      const selectedStandards = eloStandards.map((eloStandard) => ({
        id: eloStandard._id,
        ...eloStandard,
      }))

      updateAlignmentData({
        subject: data.subject,
        curriculum: data.standard?.curriculum,
        curriculumId: data.standard?.id,
        grades: data.grades,
        standards: selectedStandards,
      })
    }
    setShowStandardsModal(false)
  }

  const handleShowStandardsModal = () => {
    if (curriculumStandardsLoading) {
      return
    }
    if (isUserNotVerified) {
      toggleVerifyEmailModal(true)
      return
    }
    setShowStandardsModal(true)
  }

  const pdlStandardProps = {
    pdlCurriculumId,
    pdlAdoptYear,
    pdlCurriculumName,
    pdlSubjectLabel,
    pdlSubjectId,
    pdlGrades,
    grades,
    subject,
  }

  const [isLoading, setIsLoading] = useState(false)

  return (
    <>
      {showStandardsModal && (
        <StandardsModal
          t={i18translate}
          defaultSubject={subject}
          defaultGrades={grades}
          defaultStandards={standards}
          defaultStandard={{
            curriculum,
            id: curriculumId,
          }}
          pdlStandardProps={pdlStandardProps}
          visible={showStandardsModal}
          curriculums={allCurriculums}
          onApply={handleApply}
          setSubject={updateSubject}
          onCancel={() => setShowStandardsModal(false)}
          getCurriculumStandards={getCurriculumStandards}
          curriculumStandardsLoading={curriculumStandardsLoading}
          alignmentIndex={0}
          showAllInterestedCurriculums
          setIsLoading={setIsLoading}
        />
      )}
      {isLoading && <PortalSpinner />}
      <StyledDataInputWrapper>
        <Divider />
        <br />
        <Row type="flex" gutter={16}>
          <Col span={5}>
            <StyledFieldLabel>
              Subject<StyledRequired>*</StyledRequired>
            </StyledFieldLabel>
            <StyledSelectInput
              onChange={updateSubject}
              data-cy="selectSubject"
              placeholder="Select Subject"
              getPopupContainer={(trigger) => trigger.parentNode}
              value={subject || undefined}
            >
              {selectsData.allSubjects.map(({ text, value }) => (
                <Select.Option key={value} value={value}>
                  {text}
                </Select.Option>
              ))}
            </StyledSelectInput>
          </Col>
          <Col span={7}>
            <StyledFieldLabel>
              Grade<StyledRequired>*</StyledRequired>
            </StyledFieldLabel>
            <StyledSelectInput
              mode="multiple"
              data-cy="selectGrades"
              showArrow
              showSearch
              placeholder="Select Grade"
              getPopupContainer={(trigger) => trigger.parentNode}
              value={grades}
              maxTagCount={4}
              onChange={(_grades) => {
                updateAlignmentData({ grades: _grades })
              }}
            >
              {selectsData.allGrades.map(({ text, value }) => (
                <Select.Option key={text} value={value}>
                  {text}
                </Select.Option>
              ))}
            </StyledSelectInput>
          </Col>
          <Col span={5}>
            <StyledFieldLabel>Standard Set</StyledFieldLabel>
            <StyledSelectInput
              showSearch
              filterOption
              labelInValue
              optionFilterProp="children"
              placeholder="Select Standard Set"
              data-cy="selectSdtSet"
              getPopupContainer={(trigger) => trigger.parentNode}
              value={
                curriculumId && curriculum
                  ? { value: curriculumId, label: curriculum }
                  : undefined
              }
              onChange={(selectedCurriculum) => {
                updateAlignmentData({
                  curriculumId: selectedCurriculum.key,
                  curriculum: selectedCurriculum.label,
                })
              }}
            >
              {formattedCuriculums.map(
                ({ value, text, disabled: disabledOption }) => (
                  <Select.Option value={value} disabled={disabledOption}>
                    {text}
                  </Select.Option>
                )
              )}
            </StyledSelectInput>
          </Col>
          <Col span={7}>
            <StyledFieldLabel>
              Standards <StyledOptionalText>(OPTIONAL)</StyledOptionalText>{' '}
            </StyledFieldLabel>
            <Tooltip
              title={
                hasEmptyFields
                  ? i18translate('component.options.browseDisabled')
                  : ''
              }
              id="standards-browse-tooltip"
            >
              <div
                style={{ position: 'relative' }}
                aria-describedby="standards-browse-tooltip"
              >
                <StyledSelectInput
                  bg="white"
                  mode="multiple"
                  data-cy="selectStandards"
                  placeholder="Select Standards"
                  filterOption={false}
                  value={standardsArr}
                  optionLabelProp="title"
                  maxTagCount={4}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  onFocus={() => handleSearchStandard('')}
                  onSearch={handleSearchStandard}
                  onSelect={handleStandardSelect}
                  onDeselect={handleStandardDeselect}
                  disabled={hasEmptyFields}
                >
                  {!curriculumStandardsLoading &&
                    curriculumStandardsELO?.length > 0 &&
                    curriculumStandardsELO.map((el) => (
                      <Select.Option
                        title={el.identifier}
                        key={el._id}
                        value={el.identifier}
                        obj={el}
                        style={{ whiteSpace: 'normal' }}
                      >
                        <div>
                          <div>
                            <b>{el.identifier}</b>
                          </div>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: sanitizeHtml(el.description),
                            }}
                          />
                        </div>
                      </Select.Option>
                    ))}
                </StyledSelectInput>

                <StyledBrowseIconWrapper
                  disabled={curriculumStandardsLoading || hasEmptyFields}
                >
                  <IconExpandBox
                    onClick={() => {
                      if (hasEmptyFields) return
                      handleShowStandardsModal()
                    }}
                  />
                </StyledBrowseIconWrapper>
              </div>
            </Tooltip>
          </Col>
        </Row>
      </StyledDataInputWrapper>
    </>
  )
}

const enhance = compose(
  withNamespaces('assessment'),
  connect(
    (state, props) => ({
      formattedCuriculums: getAllFormattedCurriculumsSelector(state, {
        subject: props?.alignment?.subject,
      }),
      curriculumStandards: getStandardsListSelector(state),
      curriculumStandardsLoading: standardsSelector(state)?.loading,
      curriculumsData: get(state, 'dictionaries.curriculums.curriculums', []),
      isUserNotVerified: isUserNotVerifiedSelector(state),
    }),
    {
      getCurriculumStandards: getDictStandardsForCurriculumAction,
      toggleVerifyEmailModal: toggleVerifyEmailModalAction,
    }
  )
)

export default enhance(StandardsInput)
